import { useEffect, useState } from 'react';
import { api, type MisuCancelKind } from '../lib/api';
import { ErrorBox, dash, money, MoneyInput, type Row } from './DataTable';
import Modal from './Modal';
import { canScreen, useMe } from '../lib/me';

/**
 * 청구·수금 상태 편집 — **미수금 탭과 거래처별 정산이 같이 쓰는 한 벌**(2026-10-02 way).
 *
 * 폼을 두 벌 두면 상태 목록·저장 칸이 한쪽만 바뀐다 — 저장은 늘 `saveResultMisu` 하나로 간다
 * (`(recv_id, drive_mon)` upsert — **행이 없으면 새로 만든다**. 레거시 `usp_ResultMisu_SaveCust`
 * 「미수금 등록」도 거래처 정산 쪽에서 행을 만들었다).
 *
 * ⚠️ `req_status` 와 `bank_status` 는 **의미가 겹친다** — 초기엔 `req_status` 하나로 청구·입금을
 *    다 표현했고(그래서 `입금완료`가 거기 14,042건), 2022-11 경 `bank_status` 를 분리했다.
 *    정규화하려면 과거 데이터 마이그레이션이 필요해 **둘 다 그대로 두고 나란히** 보여준다.
 *
 * 상태 뜻(plan/accounting.md · misuSync.ts · salaryGen.ts):
 * - **청구대상** = 영업이 「청구 요청」(계산서·카드·현금)을 올린 상태. 선지급·영업정산 게이트가 여기서 열리고
 *   청구 금액이 잠긴다(2026-10-16 회의 #36 — 청구임시 폐지 · 서버 `billLock.ts`).
 * - **청구완료** = 계산서 발행(발행되면 misuSync 가 자동으로 바꾼다).
 *
 * 마감 검사가 없다 — 레거시 저장 SP 전부 `ResultFinish` 를 안 본다. 돈을 언제 받았는지는
 * 마감과 무관한 사실이라 그게 맞다(settlement-customer.md §4(e)).
 *
 * 🔴 **두 갈래(`scope`)** — 2026-10-02 way 「거래처별 정산은 영업부 전담화면이라 청구대상만 · 입금체크도 회계팀 ·
 *    미수금에서 다 관리」. 서버 `saveResultMisu` 가 같은 규칙을 강제한다(여기는 버튼만).
 * - `sales`(거래처별 정산) = 보기 + 청구 요청 뒤(청구대상~입금완료)면 **「취소·수정 승인요청」**(「취소」/「수정」 · 사유 필수 ·
 *   수정은 바뀐 금액·수정 내용 필수 → 회계가 「회계 › 발행 요청」에서 승인/반려 · 서버 `misuCancel.ts` · 10-19 일원화).
 *   대기 중이면 「요청 중」 + 내 요청이면 「요청 취소」, 결과(승인·반려)는 한 줄.
 *   청구대상 직접 표시·해제는 10-16 회의 #36 으로 없앴다 — 청구는 「청구 요청」(발행 요청 창)으로만 건다.
 * - `misu`(미수금 · 미청구) = 회계 화면 입력(또는 관리자)이면 전체 편집, 아니면 보기만.
 */

/** 청구상태 고르기. 입금예정·입금완료는 입금상태 칸으로만(10-16 way) — 옛 줄 값은 「(옛 값)」으로 보여 그대로 저장된다. */
export const REQ_STATUS = ['', '청구대상', '청구완료'];
export const BANK_STATUS = ['', '입금예정', '입금완료'];

type Form = {
  reqStatus: string;
  bankStatus: string;
  reqDate: string;
  depositDate: string;
  reqCallDate: string;
  misuNum: string;
  misuComment: string;
  preDeposit: boolean;
  isAfterDeposit: boolean;
};

const fromRow = (r: Row | Record<string, unknown>): Form => ({
  reqStatus: String(r.req_status ?? ''),
  bankStatus: String(r.bank_status ?? ''),
  reqDate: String(r.req_date ?? ''),
  depositDate: String(r.deposit_date ?? ''),
  reqCallDate: String(r.req_call_date ?? ''),
  misuNum: String(r.misu_num ?? ''),
  misuComment: String(r.misu_comment ?? ''),
  preDeposit: r.pre_deposit === true,
  isAfterDeposit: r.is_after_deposit === true,
});

export type MisuScope = 'sales' | 'misu';

/** 청구 요청 이후 — 영업은 「취소·수정 승인요청」만. 서버 `misuCancel.AFTER_ACCT` 와 같다. */
export const AFTER_ACCT = ['청구대상', '청구완료', '입금예정', '입금완료'];

/** 이 사람이 이 화면에서 무엇을 할 수 있나(버튼 이름·창 모드). 강제는 서버다. */
export function misuAccess(me: Parameters<typeof canScreen>[0], scope: MisuScope): boolean {
  return scope === 'sales' ? canScreen(me, 'settle') : canScreen(me, 'accounting');
}

/**
 * `row` 는 `recv_id`·`drive_mon`·`cust_name`·`cost_cust_total` 만 있으면 된다.
 * `row.result_misu_id` 가 있으면(미수금 목록) 그 줄 값으로 바로 연다.
 * 없으면(거래처별 정산 — 목록엔 상태 두 칸뿐) **서버에서 원장 한 줄을 읽어 채운다** —
 * 🔴 안 읽고 열면 저장이 미수번호·메모·선입금을 빈 값으로 덮는다. 읽기 전에는 저장을 막는다.
 * `sales` 는 지금 상태·마지막 변경자로 버튼을 정하므로 **늘 서버에서 읽는다**.
 */
export default function MisuEditForm({
  row, scope, onClose, onSaved,
}: { row: Row; scope: MisuScope; onClose: () => void; onSaved: () => void }) {
  const recvId = Number(row.recv_id);
  const driveMon = String(row.drive_mon ?? '');
  const hasFull = scope === 'misu' && row.result_misu_id !== undefined && row.result_misu_id !== null;
  const { me } = useMe();

  const [v, setV] = useState<Form>(() => fromRow(hasFull ? row : {}));
  const [updatedBy, setUpdatedBy] = useState<string>(hasFull ? String(row.updated_by ?? '') : '');
  const [ready, setReady] = useState(hasFull);
  /** 원장에 이 접수·달 줄이 아직 없다 — 저장하면 새로 생긴다. */
  const [isNew, setIsNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [log, setLog] = useState<Row[]>([]);
  /** 이 접수·월의 마지막 청구 취소 요청(대기면 그것) — 영업 버튼·반려 사유를 정한다. */
  const [cancel, setCancel] = useState<Row | null>(null);
  /** 취소·수정 요청 창 「세금계산서 발행일자」 기본값 = 기존 계산서 작성일자(서버 `invoiceDateOf`). */
  const [invoiceDate, setInvoiceDate] = useState<string | null>(null);
  const [asking, setAsking] = useState(false);
  /** 요청을 올리거나 거둔 뒤 다시 읽는다. */
  const [reload, setReload] = useState(0);

  const set = (k: keyof Form, val: string | boolean) => setV((c) => ({ ...c, [k]: val }));

  useEffect(() => {
    if (hasFull) return;
    let alive = true;
    api
      .getResultMisu({ recvId, driveMon })
      .then((r) => {
        if (!alive) return;
        if (r.row) { setV(fromRow(r.row)); setUpdatedBy(String((r.row as Row).updated_by ?? '')); }
        else setIsNew(true);
        setCancel((r.cancel as Row | null) ?? null);
        setInvoiceDate(r.invoiceDate ?? null);
        setReady(true);
      })
      .catch((e) => { if (alive) setError(e instanceof Error ? e.message : String(e)); });
    return () => { alive = false; };
  }, [hasFull, recvId, driveMon, reload]);

  // 이력은 append-only 라 "누가 언제 무엇으로 바꿨나"가 그대로 남는다. 상태 다툼이 나면 여기가 근거다.
  useEffect(() => {
    let alive = true;
    api
      .listResultMisuLog({ recvId, driveMon })
      .then((r) => { if (alive) setLog(r.rows as Row[]); })
      .catch(() => undefined);
    return () => { alive = false; };
  }, [recvId, driveMon]);

  const allowed = misuAccess(me, scope);
  const status = v.reqStatus.trim();
  /** 영업 + 청구 요청 이후 = 「취소·수정 승인요청」 자리. */
  const salesAfter = scope === 'sales' && allowed && ready && AFTER_ACCT.includes(status);
  const pending = cancel && String(cancel.status) === '대기' ? cancel : null;
  const mine = !!pending && String(pending.requested_by ?? '').toLowerCase() === (me?.email ?? '').toLowerCase();
  const rejected = !pending && cancel && String(cancel.status) === '반려' ? cancel : null;
  const editFull = scope === 'misu' && allowed;

  async function withdraw() {
    if (!pending || !window.confirm('올린 청구 취소 요청을 거둘까요?')) return;
    setSaving(true);
    setError(null);
    try {
      await api.withdrawMisuCancel({ requestId: Number(pending.request_id) });
      setReload((n) => n + 1);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  async function save(data: Record<string, unknown>) {
    setSaving(true);
    setError(null);
    try {
      await api.saveResultMisu({ recvId, driveMon, scope, ...data });
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setSaving(false);
    }
  }

  const title = editFull ? '청구·수금 상태' : '청구 정보';
  /** 보기만 하는 까닭 한 줄 — 조용히 막으면 「버튼이 왜 없지」가 된다. */
  const viewNote = !ready || editFull || salesAfter ? null
    : scope === 'misu' ? '청구·입금 상태 변경은 회계팀(회계 화면 입력 권한)이 합니다.'
      : !allowed ? '청구 정보 변경 요청은 정산 입력 권한이 필요합니다.'
        : status === '' ? '아직 청구 요청 전입니다 — 거래처별 정산 줄의 「청구 요청」으로 올립니다.'
          : null;

  return (
    <div className="card space-y-4 p-5">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-base font-bold">{title}</h2>
        <span className="text-sm text-ink-soft">
          {dash(row.cust_name)} · 접수 {recvId}번 · {driveMon}
          <span className="ml-2">청구액 <b>{money(row.cost_cust_total ?? row.cost_cust)}</b></span>
        </span>
        <button className="btn-ghost ml-auto" onClick={onClose} disabled={saving}>{editFull ? '취소' : '닫기'}</button>
        {editFull && (
          <button className="btn-primary" onClick={() => save({ ...v })} disabled={saving || !ready}>
            {saving ? '저장 중…' : !ready ? '불러오는 중…' : '저장'}
          </button>
        )}
        {salesAfter && !pending && (
          <button className="btn-primary" onClick={() => setAsking(true)} disabled={saving}
            title="청구 요청한 뒤에는 청구 금액·상태를 바로 못 바꿉니다 — 사유를 적어 회계팀 승인을 받습니다">
            취소·수정 승인요청
          </button>
        )}
        {salesAfter && pending && mine && (
          <button className="btn-ghost" onClick={withdraw} disabled={saving}>{saving ? '처리 중…' : '요청 취소'}</button>
        )}
      </div>

      {error && <ErrorBox message={error} />}
      {scope === 'sales' && (
        <p className="break-keep rounded-lg border border-line bg-canvas px-3 py-2 text-xs text-ink-soft">
          청구는 거래처별 정산 줄의 <b>「청구 요청」</b>으로 올립니다(세금계산서·카드·현금). 올린 뒤부터 <b>선지급·기사 명세 공제가
          열리고, 청구 금액은 잠깁니다</b> — 운행료(지급)는 마감 전까지 고칠 수 있고, 입금이 끝나면 둘 다 잠깁니다.
        </p>
      )}
      {viewNote && <p className="break-keep text-xs text-amber-800">{viewNote}</p>}
      {salesAfter && !pending && (
        <p className="break-keep text-xs text-amber-800">
          「{status}」 상태라 청구 금액을 바로 못 바꿉니다 — 「취소·수정 승인요청」으로 회계팀 승인을 받습니다.
          <b>취소</b> = 계산서를 취소하고 청구 요청 전으로(같은 접수번호로 다시 청구 요청) · <b>수정</b> = 회계팀이 바뀐 금액으로 다시 발행.
        </p>
      )}
      {pending && (
        <p className="break-keep rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          <b>{String(pending.kind ?? '취소')} 요청 중(회계 확인 대기{pending.seen_name ? ` · ${String(pending.seen_name)} 님 확인 중` : ''})</b> ·{' '}
          {dash(pending.requested_name ?? pending.requested_by)} · {String(pending.requested_at ?? '').slice(0, 16).replace('T', ' ')}
          {String(pending.kind) === '수정' && <> — 금액 {money(pending.old_total)} → <b>{money(pending.fix_total)}</b> · {dash(pending.fix_note)}</>}
          {' '}— 사유: {dash(pending.reason)}
        </p>
      )}
      {rejected && (
        <p className="break-keep text-xs text-red-700">
          지난 {String(rejected.kind ?? '취소')} 요청 반려({dash(rejected.decided_name ?? rejected.decided_by)} · {String(rejected.decided_at ?? '').slice(0, 10)}):{' '}
          {String(rejected.decide_note ?? '').trim() || '사유 없음'}
        </p>
      )}
      {!pending && cancel && String(cancel.status) === '승인' && (
        <p className="break-keep text-xs text-emerald-800">
          지난 {String(cancel.kind ?? '취소')} 요청 승인({dash(cancel.decided_name ?? cancel.decided_by)} · {String(cancel.decided_at ?? '').slice(0, 16).replace('T', ' ')})
          {String(cancel.kind) === '수정' ? ` — ${money(cancel.fix_total)}원으로 다시 발행합니다(다시 발행되기 전까지 청구 금액을 고칠 수 있습니다).` : ' — 청구 요청 전으로 돌렸습니다.'}
        </p>
      )}
      {asking && (
        <Modal width="max-w-lg">
          <CancelRequestForm
            label={`${String(row.cust_name ?? '')} · 접수 ${recvId}번 · ${driveMon} · 지금 「${status}」`}
            current={row.cost_cust_total ?? row.cost_cust}
            invoiceDate={invoiceDate}
            onClose={() => setAsking(false)}
            onSend={async (a) => {
              await api.requestMisuCancel({ recvId, driveMon, ...a });
              setAsking(false);
              setReload((n) => n + 1);
            }}
          />
        </Modal>
      )}
      {isNew && editFull && (
        <p className="break-keep rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          이 접수·정산월은 아직 청구 기록이 없습니다 — 저장하면 새로 만들어집니다.
        </p>
      )}

      {editFull ? (
        <div className="grid gap-4 md:grid-cols-2">
          <Field label="청구상태" hint="청구대상 = 영업 청구 요청 · 선지급·명세 공제는 청구대상부터 열립니다">
            <select className="field" value={v.reqStatus} onChange={(e) => set('reqStatus', e.target.value)} disabled={!ready}>
              {REQ_STATUS.map((s) => (
                <option key={s} value={s}>{s === '' ? '(없음)' : s}</option>
              ))}
              {!REQ_STATUS.includes(v.reqStatus) && <option value={v.reqStatus}>{v.reqStatus} (옛 값)</option>}
            </select>
          </Field>
          <Field label="입금상태" hint="입금 여부는 이 칸에 적습니다">
            <select className="field" value={v.bankStatus} onChange={(e) => set('bankStatus', e.target.value)} disabled={!ready}>
              {BANK_STATUS.map((s) => (
                <option key={s} value={s}>{s === '' ? '(없음)' : s}</option>
              ))}
            </select>
          </Field>
          <Field label="청구일">
            <input type="date" className="field" value={v.reqDate} onChange={(e) => set('reqDate', e.target.value)} disabled={!ready} />
          </Field>
          <Field label="입금일" hint="비어 있으면 미수입니다">
            <input type="date" className="field" value={v.depositDate} onChange={(e) => set('depositDate', e.target.value)} disabled={!ready} />
          </Field>
          <Field label="청구요청일">
            <input type="date" className="field" value={v.reqCallDate} onChange={(e) => set('reqCallDate', e.target.value)} disabled={!ready} />
          </Field>
          <Field label="미수번호" hint="통합청구 묶음 번호">
            <input className="field" value={v.misuNum} onChange={(e) => set('misuNum', e.target.value)} disabled={!ready} />
          </Field>
          <div className="md:col-span-2">
            <Field label="메모">
              <input className="field" value={v.misuComment} onChange={(e) => set('misuComment', e.target.value)} disabled={!ready} />
            </Field>
          </div>
          <div className="flex gap-5 md:col-span-2">
            <label className="flex cursor-pointer items-center gap-2 text-sm">
              <input type="checkbox" checked={v.preDeposit} onChange={(e) => set('preDeposit', e.target.checked)} disabled={!ready} />
              선입금
            </label>
            <label className="flex cursor-pointer items-center gap-2 text-sm">
              <input type="checkbox" checked={v.isAfterDeposit} onChange={(e) => set('isAfterDeposit', e.target.checked)} disabled={!ready} />
              미수입금
            </label>
          </div>
        </div>
      ) : !ready ? (
        <p className="text-sm text-ink-faint">{error ? '' : '불러오는 중…'}</p>
      ) : (
        // 보기 전용 — 청구상태·청구일·입금상태·입금일·미수번호·메모 전부(way 「청구상태 모든 정보 조회 가능」).
        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm md:grid-cols-3">
          <Info label="청구상태" value={v.reqStatus || (isNew ? '(기록 없음)' : '(없음)')} />
          <Info label="청구일" value={v.reqDate} />
          <Info label="청구요청일" value={v.reqCallDate} />
          <Info label="입금상태" value={v.bankStatus} />
          <Info label="입금일" value={v.depositDate} warn={!v.depositDate && status !== '' ? '미입력 → 미수' : undefined} />
          <Info label="미수번호" value={v.misuNum} />
          <div className="col-span-2 md:col-span-3"><Info label="메모" value={v.misuComment} /></div>
          <Info label="선입금 · 미수입금" value={[v.preDeposit && '선입금', v.isAfterDeposit && '미수입금'].filter(Boolean).join(' · ')} />
          <Info label="마지막 변경" value={updatedBy} />
        </dl>
      )}

      {log.length > 0 && (
        <details className="text-xs text-ink-soft">
          <summary className="cursor-pointer">변경 이력 {log.length}건</summary>
          <ul className="mt-2 space-y-1">
            {log.map((l, i) => (
              <li key={i} className="tabular-nums">
                {String(l.updated_at ?? '').slice(0, 16).replace('T', ' ')} · {dash(l.updated_by)} —{' '}
                {dash(l.req_status)}/{dash(l.bank_status)}
                {l.deposit_date ? ` · 입금 ${l.deposit_date}` : ''}
                {l.misu_comment ? ` · ${l.misu_comment}` : ''}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

export type CancelAsk = { kind: MisuCancelKind; reason: string; fixAmount?: string; fixVatIncluded?: boolean; fixNote?: string; writtenDate: string };

/**
 * 청구 취소·수정 승인요청(10-19 일원화) — 「취소」/「수정」을 고르고 사유(필수). 수정은 바뀐 금액 + 수정 내용(필수).
 * 공용 Modal 안 · 루트 card. 회계는 「회계 › 발행 요청」의 취소·수정 칩에서 처리한다.
 */
function CancelRequestForm({ label, current, invoiceDate, onClose, onSend }: {
  label: string; current: unknown; invoiceDate?: string | null; onClose: () => void; onSend: (a: CancelAsk) => Promise<void>;
}) {
  const [kind, setKind] = useState<MisuCancelKind | null>(null);
  // 10-27 way — 발행일자도 고쳐 달라는 요청이 잦다 → 취소·수정 모두 필수 · 기본 = 기존 계산서 날짜.
  const [writtenDate, setWrittenDate] = useState(invoiceDate ?? '');
  const [reason, setReason] = useState('');
  const [amount, setAmount] = useState('');
  const [vatIncluded, setVatIncluded] = useState(false);
  const [fixNote, setFixNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // 화면 미리보기(서버와 같은 규칙: 별도 = 10% 절사 · 포함 = ÷1.1 반올림). 저장 값은 서버가 다시 계산한다.
  const n = Math.round(Number(amount.replace(/,/g, '')));
  const calc = !Number.isFinite(n) || n <= 0 ? null
    : vatIncluded ? (() => { const s = Math.floor((n * 20 + 11) / 22); return { supply: s, vat: n - s, total: n }; })()
      : { supply: n, vat: Math.floor(n / 10), total: n + Math.floor(n / 10) };
  const ready = kind !== null && reason.trim() !== '' && /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/.test(writtenDate)
    && (kind === '취소' || (calc !== null && fixNote.trim() !== ''));
  async function send() {
    if (!kind) return;
    setBusy(true);
    setErr(null);
    try {
      await onSend(kind === '취소' ? { kind, reason: reason.trim(), writtenDate }
        : { kind, reason: reason.trim(), fixAmount: amount.replace(/,/g, ''), fixVatIncluded: vatIncluded, fixNote: fixNote.trim(), writtenDate });
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }
  return (
    <div className="card space-y-3 p-5">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-base font-bold">청구 취소·수정 승인요청</h2>
        <button className="btn-ghost ml-auto" onClick={onClose} disabled={busy}>닫기</button>
        <button className="btn-primary" onClick={send} disabled={busy || !ready}>{busy ? '올리는 중…' : '요청 올리기'}</button>
      </div>
      <p className="break-keep text-xs text-ink-soft">{label} · 지금 청구액 <b>{money(current)}</b></p>
      <div className="grid grid-cols-2 gap-2">
        {(['취소', '수정'] as const).map((k) => (
          <button key={k} type="button" onClick={() => setKind(k)}
            className={`rounded-lg border px-3 py-2 text-left text-sm ${kind === k ? (k === '취소' ? 'border-red-400 bg-red-50' : 'border-violet-400 bg-violet-50') : 'border-line hover:bg-canvas'}`}>
            <b className={k === '취소' ? 'text-red-700' : 'text-violet-800'}>{k === '취소' ? '청구 취소' : '청구 수정'}</b>
            <span className="mt-0.5 block text-xs text-ink-soft">
              {k === '취소' ? '청구를 통째로 돌려보냅니다 — 승인되면 계산서가 취소되고, 금액을 고친 뒤 같은 접수번호로 다시 청구 요청합니다.'
                : '금액만 바꿔 다시 발행합니다 — 회계팀이 바뀐 금액으로 수정 발행합니다(다시 요청할 필요 없음).'}
            </span>
          </button>
        ))}
      </div>
      {err && <ErrorBox message={err} />}
      {kind === '수정' && (
        <div className="space-y-2 rounded-lg border border-violet-200 bg-violet-50/50 p-3">
          <div className="flex flex-wrap items-end gap-2">
            <label className="block">
              <span className="mb-1 block text-sm font-medium">바뀐 금액 <span className="text-xs font-normal text-red-700">필수</span></span>
              <MoneyInput className="w-44" value={amount} onChange={(v) => setAmount(v.replace(/-/g, ''))} placeholder="예) 1,500,000" />
            </label>
            <button type="button" className={`btn ${!vatIncluded ? 'bg-brand-600 text-white' : 'border border-line'}`} onClick={() => setVatIncluded(false)}>부가세 별도</button>
            <button type="button" className={`btn ${vatIncluded ? 'bg-brand-600 text-white' : 'border border-line'}`} onClick={() => setVatIncluded(true)}>부가세 포함</button>
          </div>
          {calc && <p className="text-xs">공급가액 <b>{money(calc.supply)}</b> · 세액 <b>{money(calc.vat)}</b> · 합계 <b>{money(calc.total)}</b>원</p>}
          <label className="block">
            <span className="mb-1 block text-sm font-medium">수정 내용 <span className="text-xs font-normal text-red-700">필수</span></span>
            <input className="field" maxLength={500} value={fixNote} onChange={(e) => setFixNote(e.target.value)} placeholder="예) 10/12 운행 1회 추가 — 100만 → 150만" />
          </label>
        </div>
      )}
      {kind && (
        <label className="block">
          <span className="mb-1 block text-sm font-medium">
            세금계산서 발행일자 <span className="text-xs font-normal text-red-700">필수</span>
            <span className="ml-2 text-xs font-normal text-ink-faint">
              {invoiceDate ? `기존 계산서 날짜 ${invoiceDate} — 바꿔야 하면 고쳐 주세요` : '기존 계산서 날짜를 못 찾았습니다 — 직접 넣어 주세요'}
            </span>
          </span>
          <input type="date" className="field w-44" value={writtenDate} onChange={(e) => setWrittenDate(e.target.value)} />
          <span className="mt-0.5 block text-xs text-ink-faint">
            {kind === '취소' ? '취소(마이너스) 계산서에 찍히는 날짜입니다.' : '취소(마이너스) 계산서와 다시 발행하는 계산서에 찍히는 날짜입니다.'}
          </span>
        </label>
      )}
      {kind && (
        <label className="block">
          <span className="mb-1 block text-sm font-medium">{kind} 사유 <span className="text-xs font-normal text-red-700">필수</span></span>
          <textarea className="field min-h-[4.5rem]" maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder={kind === '취소' ? '예) 거래처 오기 — 거래처를 고친 뒤 다시 발행 예정' : '예) 거래처와 금액 재협의'} autoFocus />
        </label>
      )}
      <p className="break-keep text-xs text-ink-faint">
        회계팀 「회계 › 발행 요청」 화면에 바로 팝업으로 뜹니다. 승인·반려 결과는 이 화면에 팝업으로 알려 드립니다(입금상태·입금일은 그대로).
      </p>
    </div>
  );
}

function Info({ label, value, warn }: { label: string; value: string; warn?: string }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-ink-faint">{label}</dt>
      <dd className="break-keep">
        {value ? value : <span className="text-ink-faint">—</span>}
        {warn && <span className="ml-1 text-xs text-red-700">{warn}</span>}
      </dd>
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium">
        {label}
        {hint && <span className="ml-2 text-xs font-normal text-ink-faint">{hint}</span>}
      </span>
      {children}
    </label>
  );
}
